import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '@/context/CartContext'
import { useSettings } from '@/context/SettingsContext'
import { formatPrice } from '@/utils/formatPrice'
import { initiateCheckout } from '@/utils/facebookPixel'
import { ROUTES } from '@/constants'
import { Button } from '@/components/ui/Button'

/**
 * @param {{ showCta?: boolean, onNavigate?: () => void }} props
 * `onNavigate` lets the cart drawer close itself when checkout starts.
 */
export function CartSummary({ showCta = true, onNavigate }) {
  const cart = useCart()
  const { deliveryFee: storeDeliveryFee, freeDeliveryThreshold } = useSettings()
  const navigate = useNavigate()

  const subtotal = cart.cartTotal
  // Pre-checkout estimate at the inside-Dhaka fee; checkout reprices from
  // the customer's delivery-area choice and the backend has the final say.
  const delivery =
    subtotal === 0 ? 0 : subtotal >= freeDeliveryThreshold ? 0 : storeDeliveryFee
  const total = subtotal + delivery

  const goToCheckout = () => {
    if (cart.items.length === 0) return
    initiateCheckout(cart.items, total)
    onNavigate?.()
    navigate(ROUTES.checkout)
  }

  return (
    <div className="rounded-2xl border border-charcoal-100 bg-white p-4 shadow-card sm:p-5">
      <h3 className="mb-4 text-lg font-bold text-charcoal">Order Summary</h3>
      <dl className="space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-charcoal-600">Subtotal</dt>
          <dd>{formatPrice(cart.cartTotal)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-charcoal-600">Delivery</dt>
          <dd>{delivery === 0 ? 'FREE' : formatPrice(delivery)}</dd>
        </div>
        <div className="!mt-4 flex justify-between border-t border-charcoal-100 pt-3 text-base font-bold">
          <dt>Total</dt>
          <dd className="text-coral">{formatPrice(total)}</dd>
        </div>
      </dl>


      {cart.cartTotal > 0 && cart.cartTotal < freeDeliveryThreshold && (
        <p className="mt-3 text-xs text-charcoal-600">
          Add {formatPrice(freeDeliveryThreshold - cart.cartTotal)} more for free delivery.
        </p>
      )}

      {showCta && (
        <>
          <Button
            onClick={goToCheckout}
            fullWidth
            size="lg"
            className="mt-5"
            disabled={cart.items.length === 0}
          >
            Proceed to Checkout
          </Button>
          <p className="mt-3 text-center text-xs text-charcoal-600">
            Cash on delivery · No account needed ·{' '}
            <Link to={ROUTES.trackOrder} className="font-medium text-coral hover:underline">
              Track an order
            </Link>
          </p>
        </>
      )}
    </div>
  )
}
